/**
 * Gramophone — Basil-operated lullaby interactive.
 *
 * The canonical "Concurrent Puzzle" illustration in the 2026-04-14 companion
 * book (p.7) centers on the sleeping Guardian Dog in the Liminal Atrium. The
 * dog blocks the path to the crystal chamber. To get past it the player
 * must either lull it with Minna's Purr OR have Basil wind the rusted
 * gramophone so a sleepy melody plays through the horn.
 *
 * Before this class shipped, the gramophone was a decorative prop and the
 * "dog wake" trigger was an invisible rug volume that teleported Minna
 * back to spawn — the canon's single most important teaching beat was
 * hidden inside a respawn. This class promotes the gramophone to a real
 * `InteractiveObject` that Basil can activate with the `,` key from within
 * `GRAMOPHONE_INTERACT_RADIUS` units.
 *
 * Behavior:
 * - Idle: disc is still, tone arm lifted, no particles.
 * - Playing (3.5s): disc spins, needle arm tilts down, music-note particles
 *   emit upward from the horn bell, emissive brass boost on the horn rim.
 *   Emits `GRAMOPHONE_PLAYED` at activation (the GuardianDog NPC listens
 *   on this event to resettle, and AudioManager plays the lullaby WAV).
 * - Cooldown (30s after playing ends): interact attempts ignored so the
 *   puzzle can't be skip-cheesed. Tone arm remains slightly lowered and a
 *   subtle dim pulse shows the gramophone is "recovering its spring".
 *
 * Visuals come from the `rusted_gramophone.glb` asset when it loads;
 * procedural placeholder geometry matches the silhouette so the scene
 * reads correctly even if asset loading fails.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { InstantiatedModel } from "../../systems/AssetManager";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";

/** How close Basil must be (world units) to wind the gramophone. */
export const GRAMOPHONE_INTERACT_RADIUS = 2.0;
/** How long the lullaby plays, in seconds. */
export const GRAMOPHONE_PLAY_DURATION_SECONDS = 3.5;
/** Cooldown after a play completes before the gramophone can be wound again. */
export const GRAMOPHONE_COOLDOWN_SECONDS = 30;

export type GramophoneState = "idle" | "playing" | "cooldown";

export interface GramophoneConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
  /** Override play duration (seconds). */
  playDuration?: number;
  /** Override cooldown (seconds). */
  cooldown?: number;
}

export class Gramophone extends InteractiveObject {
  /** Active gramophone playback state. Exposed for tests + debug overlays. */
  public gramophoneState: GramophoneState = "idle";

  /** Seconds remaining in the current phase (playing or cooldown). */
  private phaseRemaining = 0;

  private readonly playDuration: number;
  private readonly cooldown: number;

  private discMesh: Mesh | null = null;
  private toneArm: Mesh | null = null;
  private hornRimMat: StandardMaterial | null = null;
  private musicNoteMeshes: AbstractMesh[] = [];
  private noteEmitter: ParticleSystem | null = null;

  constructor(scene: Scene, config: GramophoneConfig) {
    const size = config.size ?? new Vector3(1.0, 1.6, 1.0);
    super(scene, { ...config, size });

    this.playDuration = config.playDuration ?? GRAMOPHONE_PLAY_DURATION_SECONDS;
    this.cooldown = config.cooldown ?? GRAMOPHONE_COOLDOWN_SECONDS;

    // Procedural placeholder silhouette — overridden when the GLB lands.
    this.buildPlaceholderGeometry();

    // Load the authored model. `loadModelOverride` hides the procedural
    // placeholders via `onModelLoaded` below and promotes the brass/horn
    // meshes to the classes used for playback visuals.
    this.loadModelOverride("/assets/models/environment/level0/rusted_gramophone.glb");
  }

  // ─── Public API ──────────────────────────────────────────────────

  /**
   * Returns true when Basil is close enough to wind the crank.
   */
  isInInteractRange(position: Vector3): boolean {
    return this.distanceTo(position) <= GRAMOPHONE_INTERACT_RADIUS;
  }

  /**
   * Begin a play cycle. No-op if the gramophone isn't idle.
   * Returns true iff the attempt consumed the interaction (caller uses
   * this to decide whether to swallow the `,` key press).
   */
  play(): boolean {
    if (this.gramophoneState !== "idle") {
      return false;
    }
    this.gramophoneState = "playing";
    this.phaseRemaining = this.playDuration;
    this.state = "active";
    this.updateMaterialForState();
    this.showPlayingVisuals();

    const rootWorld = this.rootMesh.getAbsolutePosition();
    gameEvents.emit(GameEvents.GRAMOPHONE_PLAYED, {
      key: this.key,
      at: { x: rootWorld.x, y: rootWorld.y, z: rootWorld.z },
      durationSeconds: this.playDuration,
    });
    return true;
  }

  /**
   * Per-frame tick called from the scene's game loop. Drives the phase
   * timers (playing → cooldown → idle) and advances the spin/particle
   * visuals.
   */
  tick(deltaSeconds: number, elapsed: number): void {
    if (this.gramophoneState === "idle") return;

    this.phaseRemaining = Math.max(0, this.phaseRemaining - deltaSeconds);

    if (this.gramophoneState === "playing") {
      // Spin the disc if we have one
      if (this.discMesh) {
        this.discMesh.rotation.y += deltaSeconds * Math.PI * 1.8;
      }
      // Soft pulse on the horn rim emissive
      if (this.hornRimMat) {
        const pulse = 0.25 + Math.sin(elapsed * 6) * 0.1;
        this.hornRimMat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(pulse);
      }
      // Float the music-note glow meshes upward as they fade in/out
      for (let i = 0; i < this.musicNoteMeshes.length; i++) {
        const note = this.musicNoteMeshes[i];
        const phase = (elapsed * 2 + i * 0.7) % 1.5;
        note.position.y = 1.35 + phase * 0.6;
        note.visibility = Math.max(0, 1 - phase / 1.5);
      }
      if (this.phaseRemaining <= 0) {
        this.enterCooldown();
      }
    } else if (this.gramophoneState === "cooldown") {
      // Slow rotation for another half-second for a "spring winding down"
      // cue, then the disc sits still.
      if (this.discMesh && this.phaseRemaining > this.cooldown - 0.6) {
        this.discMesh.rotation.y += deltaSeconds * Math.PI * 0.3;
      }
      if (this.phaseRemaining <= 0) {
        this.exitCooldown();
      }
    }
  }

  override onInteract(abilityId: string): boolean {
    // Basil's interact key ("," -> interact ability id). The level code
    // forwards the raw "," key to this class via the puzzle/scene's
    // onAbilityKey router.
    if (abilityId === "interact" || abilityId === "basilInteract") {
      return this.play();
    }
    return false;
  }

  /** Remaining seconds in the current phase (0 while idle). */
  get phaseSecondsRemaining(): number {
    return this.phaseRemaining;
  }

  /** True while the lullaby is audible and the GuardianDog listens. */
  get isPlaying(): boolean {
    return this.gramophoneState === "playing";
  }

  override getHighlightTarget(): AbstractMesh {
    return this.discMesh ?? this.rootMesh;
  }

  override dispose(): void {
    this.noteEmitter?.dispose();
    this.noteEmitter = null;
    super.dispose();
  }

  // ─── Private ─────────────────────────────────────────────────────

  private enterCooldown(): void {
    this.gramophoneState = "cooldown";
    this.phaseRemaining = this.cooldown;
    this.state = "idle";
    this.updateMaterialForState();
    this.hidePlayingVisuals();
    gameEvents.emit(GameEvents.GRAMOPHONE_STOPPED, { key: this.key });
  }

  private exitCooldown(): void {
    this.gramophoneState = "idle";
    this.phaseRemaining = 0;
    this.state = "idle";
    this.updateMaterialForState();
  }

  private showPlayingVisuals(): void {
    // Reveal music-note meshes (flipped off in idle)
    for (const note of this.musicNoteMeshes) {
      note.isVisible = true;
      note.visibility = 1;
    }
    // Start particle emitter for extra music-note dust
    if (this.noteEmitter && !this.noteEmitter.isStarted()) {
      this.noteEmitter.start();
    }
    // Lower the tone arm so the needle "rests on the disc"
    if (this.toneArm) {
      this.toneArm.rotation.z = -0.25;
    }
  }

  private hidePlayingVisuals(): void {
    for (const note of this.musicNoteMeshes) {
      note.isVisible = false;
    }
    if (this.noteEmitter?.isStarted()) {
      this.noteEmitter.stop();
    }
    if (this.toneArm) {
      this.toneArm.rotation.z = 0;
    }
    if (this.hornRimMat) {
      this.hornRimMat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.08);
    }
  }

  private buildPlaceholderGeometry(): void {
    const scene = this.scene;

    // Cabinet (wooden base)
    const cabinet = MeshBuilder.CreateBox(
      `gramophone_${this.key}_cabinet`,
      { width: 0.9, height: 0.55, depth: 0.75 },
      scene,
    );
    cabinet.parent = this.rootMesh;
    cabinet.position.y = 0.275;
    const cabinetMat = new StandardMaterial(`gramophone_${this.key}_cabinetMat`, scene);
    cabinetMat.diffuseColor = Color3.FromHexString("#3a2416");
    cabinetMat.specularColor = new Color3(0.05, 0.04, 0.03);
    cabinet.material = cabinetMat;

    // Turntable disc
    const disc = MeshBuilder.CreateCylinder(
      `gramophone_${this.key}_disc`,
      { diameter: 0.66, height: 0.04, tessellation: 32 },
      scene,
    );
    disc.parent = this.rootMesh;
    disc.position.y = 0.58;
    const discMat = new StandardMaterial(`gramophone_${this.key}_discMat`, scene);
    discMat.diffuseColor = new Color3(0.05, 0.03, 0.03);
    discMat.specularColor = new Color3(0.15, 0.12, 0.08);
    disc.material = discMat;
    this.discMesh = disc;

    // Tone arm (thin elongated box rooted at the back-right corner)
    const arm = MeshBuilder.CreateBox(
      `gramophone_${this.key}_arm`,
      { width: 0.04, height: 0.02, depth: 0.32 },
      scene,
    );
    arm.parent = this.rootMesh;
    arm.position = new Vector3(0.18, 0.62, -0.1);
    arm.rotation.y = -0.4;
    const armMat = new StandardMaterial(`gramophone_${this.key}_armMat`, scene);
    armMat.diffuseColor = Color3.FromHexString(COLORS.BRASS);
    armMat.specularColor = new Color3(0.3, 0.25, 0.1);
    arm.material = armMat;
    this.toneArm = arm;

    // Horn (flared cylinder)
    const horn = MeshBuilder.CreateCylinder(
      `gramophone_${this.key}_horn`,
      { diameterTop: 0.95, diameterBottom: 0.18, height: 1.05, tessellation: 28 },
      scene,
    );
    horn.parent = this.rootMesh;
    horn.position = new Vector3(0.05, 1.15, -0.05);
    horn.rotation = new Vector3(Math.PI / 4.2, 0, 0);
    const hornMat = new StandardMaterial(`gramophone_${this.key}_hornMat`, scene);
    hornMat.diffuseColor = Color3.FromHexString(COLORS.COPPER);
    hornMat.specularColor = new Color3(0.6, 0.45, 0.2);
    hornMat.emissiveColor = new Color3(0.04, 0.025, 0.01);
    horn.material = hornMat;
    this.hornRimMat = hornMat;

    // Music-note glow meshes, hidden by default
    const noteMat = new StandardMaterial(`gramophone_${this.key}_noteMat`, scene);
    noteMat.diffuseColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN);
    noteMat.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN).scale(1.2);
    noteMat.specularColor = Color3.Black();
    noteMat.alpha = 0.85;

    const notePositions = [
      new Vector3(-0.55, 1.35, -0.05),
      new Vector3(-0.70, 1.55, -0.05),
      new Vector3(-0.45, 1.70, -0.05),
      new Vector3(-0.30, 1.55, -0.02),
    ];
    for (let i = 0; i < notePositions.length; i++) {
      const note = MeshBuilder.CreateSphere(
        `gramophone_${this.key}_note_${i}`,
        { diameter: 0.08, segments: 6 },
        scene,
      );
      note.parent = this.rootMesh;
      note.position = notePositions[i];
      note.material = noteMat;
      note.isVisible = false;
      this.musicNoteMeshes.push(note);
    }

    // Particle system: additional softer music dust spilling out of the bell.
    try {
      const emitter = MeshBuilder.CreateBox(
        `gramophone_${this.key}_noteEmitter`,
        { size: 0.01 },
        scene,
      );
      emitter.parent = this.rootMesh;
      emitter.position = new Vector3(-0.35, 1.45, -0.05);
      emitter.isVisible = false;

      const ps = new ParticleSystem(`gramophone_${this.key}_notesPS`, 48, scene);
      ps.particleTexture = new Texture(
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=",
        scene,
      );
      ps.emitter = emitter;
      ps.minEmitBox = new Vector3(-0.02, 0, -0.02);
      ps.maxEmitBox = new Vector3(0.02, 0.02, 0.02);
      ps.color1 = new Color4(0.2, 1.0, 0.55, 1.0);
      ps.color2 = new Color4(0.55, 0.8, 0.4, 1.0);
      ps.colorDead = new Color4(0.1, 0.3, 0.15, 0.0);
      ps.minSize = 0.05;
      ps.maxSize = 0.14;
      ps.minLifeTime = 0.8;
      ps.maxLifeTime = 1.5;
      ps.emitRate = 14;
      ps.direction1 = new Vector3(-0.6, 1.2, -0.2);
      ps.direction2 = new Vector3(0.2, 2.0, 0.3);
      ps.gravity = new Vector3(0, 0.3, 0);
      ps.minAngularSpeed = -1.2;
      ps.maxAngularSpeed = 1.2;
      this.noteEmitter = ps;
    } catch {
      // ParticleSystem texture load is best-effort. The glowing sphere
      // meshes above provide the primary read on the "music is playing"
      // feedback even if particles fail to init.
    }
  }

  protected override onModelLoaded(instance: InstantiatedModel): void {
    // Promote GLB meshes to the classes we animate. The authored mesh names
    // are set in the Blender export (see `assets/models/environment/level0/
    // rusted_gramophone.blend`): Gramo_TurntableDisc, Gramo_ToneArm,
    // Gramo_HornRim, Gramo_MusicNote_*.
    for (const mesh of instance.meshes) {
      if (!(mesh instanceof Mesh) && !(mesh instanceof TransformNode)) continue;
      const name = mesh.name;

      if (name.includes("TurntableDisc")) {
        this.discMesh = mesh as Mesh;
      } else if (name.includes("ToneArm")) {
        this.toneArm = mesh as Mesh;
      } else if (name.includes("HornRim")) {
        const m = (mesh as Mesh).material;
        if (m instanceof StandardMaterial) {
          this.hornRimMat = m;
        }
      } else if (name.includes("MusicNote")) {
        // Music notes from the authored model are hidden by default; the
        // gramophone reveals them while playing.
        mesh.isVisible = false;
        this.musicNoteMeshes.push(mesh);
      }
    }
    // Hide the procedural placeholder meshes — the InteractiveObject base
    // toggled them visible; we re-hide everything that isn't an authored
    // mesh so the final art isn't layered on top of the placeholder.
    for (const child of this.rootMesh.getChildMeshes()) {
      const isAuthored = instance.meshes.some((m) => m === child);
      if (!isAuthored && child.name.startsWith(`gramophone_${this.key}_`)) {
        child.isVisible = false;
      }
    }
  }
}
